import React, { useEffect, useState } from "react"
import { Container, Row, Col, Tabs, Tab } from "react-bootstrap"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import Swal from "sweetalert2"

import { editClient, readClient } from "../actions/other/clientsActions"
import { getPoliciesByClient } from "../actions/other/policiesActions"
import { RootState, AppDispatch } from "../store"

import { CustomDataTable } from "../components/design/Datatables/CustomDataTable"
import Loader from "../components/loaders/Loader"
import ClientForm from "../components/forms/ClientForm"

export interface Client {
	_id?: string
	id?: string
	nombreCompleto?: string
	direccion?: string
	poblacion?: string
	cifNif?: string
	arrayPhones?: string[]
	arrayBanks?: string[]
	arrayNotes?: any[]
}
export interface Policy {
	_id?: string
	id?: string
	numero?: number
	nifCliente?: string
	nombreCliente?: string
	fechaEfecto?: string
	fechaLimiteTemporal?: string
	nombreCompania?: string
	idEstado?: string
	riesgo?: string
	subramo?: string
	formaPago?: string
}

export default function ClientDetail() {
	const { t } = useTranslation()
	const { id } = useParams()
	const dispatch: AppDispatch = useDispatch()
	const location = useLocation()
	const navigate = useNavigate()

	const { client, resultclient, loadingdata } = useSelector((state: RootState) => state.clients)
	const { policies, loading } = useSelector((state: RootState) => state.policies)

	const [isEditing, setIsEditing] = useState(false)
	const [clientSelected, setClientSelected] = useState<Client | null>(null)

	/* ===============================
	   CARGAR CLIENTE
	=============================== */

	useEffect(() => {
		if (id) {
			dispatch(readClient(id) as any)
			dispatch(getPoliciesByClient(id) as any)
		}
	}, [id, dispatch])

	useEffect(() => {
		if (client) {
			setClientSelected(client)
		}
	}, [client])

	/* ===============================
	   INPUTS FORM CLIENTE
	=============================== */

	const handleInputChange = (e: any) => {
		const { name, value } = e.target
		setClientSelected(prev => (prev ? { ...prev, [name]: value } : null))
	}

	/* ===============================
	   GUARDAR CLIENTE
	=============================== */

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		setIsEditing(true)
		dispatch(editClient(clientSelected, clientSelected?.id) as any)
	}

	/* ===============================
	   NAVEGAR A POLIZA DETALLE
	=============================== */
	const pathParts = location.pathname.split("/")
	const base = pathParts[1] // admin | clerk | commercial etc.

	const selectPolicy = (policy: Policy) => {
		navigate(`/${base}/polizas/${policy.id}`)
	}

	useEffect(() => {
		if (!resultclient) return

		dispatch({ type: "CLIENT_RESET_RESULT" })
		if (resultclient === "editsuccess") {
			// Recargar para obtener _id/ebrokerId asignados por el backend.
			// Sin esto la nota sigue pareciendo editable y se puede reenviar a ebroker.
			if (id) {
				dispatch(readClient(id) as any)
			}
			Swal.fire({
				icon: "success",
				confirmButtonText: t("global.great"),
				text: t("clientTranslate.editSuccess"),
			}).then(() => {
				setIsEditing(false)
			})
		}
	}, [resultclient, dispatch, t, id])

	const columnsPolicy = [
		{
			name: t("policyTranslate.numero"),
			width: "150px",
			cell: (policy: Policy) => (
				<div className="cursor text-start" onClick={() => selectPolicy(policy)}>
					{policy?.numero || "—"}
				</div>
			),
			selector: (policy: Policy) => policy?.numero || "—",
			sortable: true,
		},
		{
			name: t("policyTranslate.nombreCliente"),
			cell: (policy: Policy) => (
				<div className="cursor text-start" onClick={() => selectPolicy(policy)}>
					{policy?.nombreCliente || "—"}
				</div>
			),
			selector: (policy: Policy) => policy?.nombreCliente || "—",
			sortable: true,
		},
		{
			name: t("policyTranslate.fechaEfecto"),
			cell: (policy: Policy) => (
				<div className="cursor text-start" onClick={() => selectPolicy(policy)}>
					{new Intl.DateTimeFormat('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Europe/Madrid' }).format(new Date(policy?.fechaEfecto || "—"))}
				</div>
			),
			selector: (policy: Policy) => policy?.fechaEfecto || "—",
			sortable: true,
		}, {
			name: t("policyTranslate.compania"),
			cell: (policy: Policy) => (
				<div className="cursor text-start" onClick={() => selectPolicy(policy)}>
					{policy?.nombreCompania || "—"}
				</div>
			),
			selector: (policy: Policy) => policy?.nombreCompania || "—",
			sortable: true,
		}, {
			name: t("policyTranslate.estado"),
			cell: (policy: Policy) => (
				<div className="cursor" onClick={() => selectPolicy(policy)}>
					{policy?.idEstado === "A" ?
						t("policyTranslate.anulado") :
						policy?.idEstado === "V" ?
							t("policyTranslate.enVigor") :
							policy?.idEstado === "S" ?
								t("policyTranslate.suspension") :
								"—"
					}
				</div>
			),
			selector: (policy: Policy) => policy?.idEstado || "—",
			sortable: true,
		}, {
			name: t("policyTranslate.riesgo"),
			cell: (policy: Policy) => (
				<div className="cursor text-start" onClick={() => selectPolicy(policy)}>
					{policy?.riesgo || "—"}
				</div>
			),
			selector: (policy: Policy) => policy?.riesgo || "—",
			sortable: true,
		}, {
			name: t("policyTranslate.subramo"),
			cell: (policy: Policy) => (
				<div className="cursor text-start" onClick={() => selectPolicy(policy)}>
					{policy?.nombreCompania && policy?.subramo
						? `${policy.nombreCompania} - ${policy.subramo}`
						: policy?.subramo || policy?.nombreCompania || "—"}
				</div>
			),
			selector: (policy: Policy) => policy?.subramo || "—",
			sortable: true,
		}, {
			name: t("policyTranslate.formaPago"),
			cell: (policy: Policy) => (
				<div className="cursor text-start" onClick={() => selectPolicy(policy)}>
					{policy?.formaPago || "—"}
				</div>
			),
			selector: (policy: Policy) => policy?.formaPago || "—",
			sortable: true,
		}
	]

	return (
		<Container fluid className="p-1 py-5 p-md-5">
			<Row>
				<Col>
					<h1>{t("clientTranslate.clientDetails")}</h1>
				</Col>
			</Row>
			<Row className="my-3">
				<Col>
					<Tabs
						defaultActiveKey="general"
						id="client-tabs"
						className="mb-0"
						fill
					>
						<Tab eventKey="general" title={t('clientTranslate.client')}>
							<div className="border border-top-0 rounded-bottom p-4 bg-white">
								{loadingdata
									?
									<Loader />
									:
									<Row>
										<Col xs={12} lg={4} className="mb-3">
											<h5>
												<h5>{t("clientTranslate.nombre")}</h5>
											</h5>
											<hr />
											<strong>{client?.nombreCompleto || "—"}</strong>
										</Col>

										<Col xs={12} lg={4} className="mb-3">
											<h5>
												{client?.documentacion?.tipoDocumento || "—"}
											</h5>
											<hr />
											{client?.documentacion?.numeroDocumento || "—"}
										</Col>

										<Col xs={12} lg={4} className="mb-3">
											<h5>{t("clientTranslate.telefono")}</h5>
											<hr />
											{(client?.arrayPhones ?? []).join(", ") || "—"}
										</Col>

										<Col xs={12} lg={8} className="mb-3">
											<h5>{t("clientTranslate.direccion")}</h5>
											<hr />
											{client?.direccion
												? `${client.direccion.direccion}, ${client.direccion.codigoPostal} ${client.direccion.poblacion}`
												: "—"}
										</Col>

										<Col xs={12} lg={4} className="mb-3">
											<h5>{t("clientTranslate.municipio")}</h5>
											<hr />
											{client?.direccion?.municipio || "—"}
										</Col>

										<Col xs={12} lg={6} className="mb-3">
											<h5>{t("clientTranslate.email")}</h5>
											<hr />
											{(client?.arrayEmails ?? []).join(", ") || "—"}
										</Col>

										<Col xs={12} lg={3} className="mb-3">
											<h5>{t("clientTranslate.medioCobro")}</h5>
											<hr />
											{client?.idMedioCobro || "—"}
										</Col>

										<Col xs={12} lg={3} className="mb-3">
											<h5>{t("clientTranslate.cuentaBancaria")}</h5>
											<hr />
											{(client?.arrayBanks ?? []).join(", ") || "—"}
										</Col>
									</Row>
								}
							</div>
						</Tab>
						<Tab eventKey="comentarios" title={t('clientTranslate.comments')}>
							<div className="border border-top-0 rounded-bottom px-1 py-2 bg-white">
								<ClientForm
									state={clientSelected}
									handleInputChange={handleInputChange}
									submit={handleSubmit}
									isEditing={isEditing}
									loadingdata={loadingdata}
								/>
							</div>
						</Tab>
						<Tab eventKey="polizas" title={t('clientTranslate.policies')}>
							<div className="border border-top-0 rounded-bottom px-1 py-2 bg-white">
								{loading ? (
									<Loader />
								) : policies?.length > 0 ? (
									<CustomDataTable
										columns={columnsPolicy}
										data={policies}
										pagination={true}
									/>
								) : (
									<p className="text-center m-0">{t("policyTranslate.thereisnotpolicy")}</p>
								)}
							</div>
						</Tab>
					</Tabs>
				</Col>

			</Row>
		</Container>
	)
}
